import { createAsyncThunk } from "@reduxjs/toolkit";
import { supplierResponsesAllService } from "@/shared/services/api/supplier-responses-all-service";
import { ISpecificationSupplierProductsRequest } from "@/shared/services/interface/requests/ISupplierResponsesAllRequest";
import { specificationService } from "@/shared/services/api/specification-service";
import { IGetScreenTimeStatsRequest } from "@/shared/services/interface/requests/IReportRequest";
import { ISpecificationCategoryAndItemGetRequest } from "@/shared/services/interface/requests/specification";
import { supplierResponseSingleService } from "@/shared/services/api/supplier-response-single-service";

export const getSupplierResponseProductsThunk = createAsyncThunk(
  "supplierResponseSingle/getSupplierResponseProducts",
  async (
    request: ISpecificationSupplierProductsRequest,
    { rejectWithValue }
  ) => {
    try {
      const response =
        await supplierResponsesAllService.getSpecificationProducts(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const getSupplierResponseScreenTimeStatsThunk = createAsyncThunk(
  "supplierResponseSingle/getSupplierResponseScreenTimeStats",
  async (request: IGetScreenTimeStatsRequest, { rejectWithValue }) => {
    try {
      const response =
        await specificationService.getSpecificationScreenTimeStats(request);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const getSupplierResponseCategoriesThunk = createAsyncThunk(
  "supplierResponseSingle/getSupplierResponseCategories",
  async (
    request: {
      companyId: string;
      specificationId: string;
      includeCommentCount?: boolean;
      includeComments?: boolean;
      productId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await specificationService.getSpecificationCategoryAndItem({
          companyId: request.companyId,
          specificationId: request.specificationId,
          includeCommentCount: request.includeCommentCount ?? true,
          includeComments: request.includeComments ?? true,
          productId: request.productId,
        });
      // API returns { success: true, data: [...] } format
      if (response.success && response.data) {
        return response.data as Array<any>;
      }
      return response.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const getItemCommentsThunk = createAsyncThunk(
  "supplierResponseSingle/getItemComments",
  async (
    request: {
      itemId: string;
      productId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await supplierResponseSingleService.getItemComments(
        request.itemId,
        request.productId
      );
      // response.data might be undefined if API returns data directly
      // In that case, response itself is the data
      const commentsData = response.data || (response as any);
      return {
        key: `${request.itemId}-${request.productId}`,
        data: commentsData,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const getSupplierItemAnswersThunk = createAsyncThunk(
  "supplierResponseSingle/getSupplierItemAnswers",
  async (
    request: {
      specificationId: string;
      productId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await supplierResponseSingleService.getSupplierItemAnswers(
        request.specificationId,
        request.productId
      );
      // response.data might be undefined if API returns data directly
      // In that case, response itself is the data
      const itemAnswersData = response.data || (response as any);
      return itemAnswersData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const getProductConflictsThunk = createAsyncThunk(
  "supplierResponseSingle/getProductConflicts",
  async (
    request: { productId: string; specificationId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await supplierResponseSingleService.getProductConflicts(
        request.productId,
        request.specificationId
      );
      const conflictsData = response.data || (response as any);
      return conflictsData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);

export const createOrUpdateResolutionThunk = createAsyncThunk(
  "supplierResponseSingle/createOrUpdateResolution",
  async (
    request: {
      itemId: string;
      productId: string;
      specificationId: string;
      finalStatus: number;
      justification?: string;
      referenceUrl?: string;
      referenceFiles?: File[];
    },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append("finalStatus", request.finalStatus.toString());
      if (request.justification) {
        formData.append("justification", request.justification);
      }
      if (request.referenceUrl) {
        formData.append("referenceUrl", request.referenceUrl);
      }
      if (request.referenceFiles && request.referenceFiles.length > 0) {
        request.referenceFiles.forEach((file) => {
          formData.append("referenceFiles", file);
        });
      }

      const response =
        await supplierResponseSingleService.createOrUpdateResolution(
          request.itemId,
          request.productId,
          request.specificationId,
          formData
        );
      const resolutionData = response.data || (response as any);
      return resolutionData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Bir hata oluştu"
      );
    }
  }
);
